import { useEffect, useMemo, useRef, useState } from 'react'
import { EventInfo } from '@/components/Graphs/GoogleMap/EventInfo'
import { mapEventsToMarkers } from '@/components/Graphs/GoogleMap/utils'
import { Event } from '@/components/Graphs/types'
import { FIREBASE_API_KEY } from '@/utils/constants'
import {
  GoogleMap,
  GoogleMapMarker,
  MapInfoBox,
  Text,
  LoaderOverlay,
} from '@saasalerts/ui'

interface AccountMapProps {
  events?: Event[]
  loading?: boolean
  noData?: boolean
  noDataText?: string
}

export const AccountMap = ({
  events,
  loading,
  noData,
  noDataText,
}: AccountMapProps) => {
  const [marker, setMarker] = useState<GoogleMapMarker | null>(null)
  const markersList = useMemo(() => mapEventsToMarkers(events ?? []), [events])

  // Used to remount map when needed
  const [mapKey, setMapKey] = useState(0)
  const bumpKey = () => setMapKey((k) => k + 1)

  // Track current DPR
  const dprRef = useRef<number>(
    typeof window !== 'undefined' ? window.devicePixelRatio : 1
  )

  // Update map when DPR (zoom/print scale) changes
  useEffect(() => {
    if (typeof window === 'undefined') return

    const dpr = window.devicePixelRatio || 1
    const query = `(resolution: ${dpr}dppx)`
    const mediaQuery = window.matchMedia(query)

    const handleDprChange = () => {
      dprRef.current = window.devicePixelRatio || 1
      bumpKey()
    }

    mediaQuery.addEventListener('change', handleDprChange)
    return () => mediaQuery.removeEventListener('change', handleDprChange)
  }, [])

  // Container ref for print functionality
  const containerRef = useRef<HTMLDivElement | null>(null)

  // Store original width/height for restore after print
  const prevSizeStyleRef = useRef<{ width?: string; height?: string } | null>(
    null
  )

  // Prepare map for printing
  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleBeforePrint = async () => {
      const container = containerRef.current
      if (!container) return

      // Lock container size
      const width = container.offsetWidth
      const height = container.offsetHeight

      prevSizeStyleRef.current = {
        width: container.style.width,
        height: container.style.height,
      }

      Object.assign(container.style, {
        width: `${width}px`,
        height: `${height}px`,
      })

      // Sync DPR and re-render map for proper alignment
      dprRef.current = window.devicePixelRatio || 1
      bumpKey()

      // Wait for layout to stabilize (2 frames + 500ms delay)
      await new Promise((r) =>
        requestAnimationFrame(() => requestAnimationFrame(r))
      )
      await new Promise((r) => setTimeout(r, 500))
    }

    const handleAfterPrint = () => {
      // Restore size
      if (containerRef.current && prevSizeStyleRef.current) {
        Object.assign(containerRef.current.style, prevSizeStyleRef.current)
      }
      // Re-sync DPR
      dprRef.current = window.devicePixelRatio || 1
      bumpKey()
    }

    // Restore Ctrl/Cmd + P shortcut for browsers that skip beforeprint
    const onKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes('MAC')
      const isPrintShortcut =
        (isMac && e.metaKey && e.key.toLowerCase() === 'p') ||
        (!isMac && e.ctrlKey && e.key.toLowerCase() === 'p')
      if (isPrintShortcut) {
        e.preventDefault()
        handleBeforePrint().then(() => window.print())
      }
    }

    window.addEventListener('beforeprint', handleBeforePrint)
    window.addEventListener('afterprint', handleAfterPrint)
    window.addEventListener('keydown', onKeyDown, { capture: true })

    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint)
      window.removeEventListener('afterprint', handleAfterPrint)
      window.removeEventListener('keydown', onKeyDown, { capture: true })
    }
  }, [])

  const eventInfo = (id?: string) => {
    const e = events?.find((ev) => ev.id === id)
    return e ? (
      <EventInfo onCloseClick={() => setMarker(null)} event={e} />
    ) : null
  }

  if (noData) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Text color="secondary">{noDataText || 'No data available'}</Text>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="map_box relative meticulous-ignore">
      {loading && <LoaderOverlay loaderProps={{ size: 'large' }} />}
      <GoogleMap
        key={mapKey}
        apiKey={FIREBASE_API_KEY}
        markersList={markersList}
        onMarkerClick={(m: GoogleMapMarker) => setMarker(m)}
        infoBoxComponent={
          <MapInfoBox ll={marker?.ll}>{eventInfo(marker?.id)}</MapInfoBox>
        }
        images={{
          cluster: {
            red: '/images/map-marker/cluster-red.png',
            green: '/images/map-marker/cluster-green.png',
            orange: '/images/map-marker/cluster-orange.png',
          },
          marker: {
            red: '/images/map-marker/point-red.png',
            green: '/images/map-marker/point-green.png',
            orange: '/images/map-marker/point-orange.png',
          },
        }}
      />
    </div>
  )
}
